import { useSearchParams } from "react-router-dom";
import { PaginatorInfo } from "../models/generic.dto";
import { useEffect, useState } from "react";
import { useGridApiRef } from "@mui/x-data-grid";

type ReturnedObject = {
    data: PaginatorInfo<any>;
    isLoading: boolean;
    error: Error | null;
    refetch: () => Promise<any>;
}

type ManageTable = {
    /** Hook to get table data `ie useGetBenefit` */
    fetchHook: (params: any) => any;

    /** Params for table data hook */
    options?: any;

    /** Reload dependencies */
    reloadDeps?: any[];
}

/**
 * A custom hook for managing table data with search parameters and pagination.
 * This hook handles data fetching, searching, and pagination state for data tables.
 * 
 * @template TData - The type of data being managed in the table
 * 
 * @param {Object} params - The parameters object
 * @param {Function} params.fetchHook - The hook function used to fetch table data
 * @param {Object} [params.options] - Additional options to pass to the fetch hook
 * @param {Array} [params.reloadDeps] - Dependencies array that triggers data reload when changed
 * 
 * @returns {Object} An object containing:
 * @returns {GridApiRef} tableAPIRef - Reference to the table API
 * @returns {TData[]} tableData - Current table data
 * @returns {TData[]} fetchedTableData - Original fetched data (used for search operations)
 * @returns {boolean} isLoading - Loading state indicator
 * @returns {number} pageCount - Total number of pages
 * @returns {string | null} searching - Search state indicator
 * @returns {Function} setTableData - Function to update table data
 * @returns {Function} setFetchedTableData - Function to update fetched table data
 * 
 * @example
 * const { tableData, isLoading, pageCount } = useManageTableWithSearchParams({
 *   fetchHook: useGetUsers,
 *   options: { role: 'admin' }
 * });
 */
export function useManageTableWithSearchParams<TData>({ fetchHook, options, reloadDeps }: ManageTable) {
	const tableAPIRef = useGridApiRef();
	const [searchParams, setSearchParams] = useSearchParams();
    const page = searchParams.get("page");
    const newData = searchParams.get("newData");
    const reloadData = searchParams.get("reloadData");
    const searching = searchParams.get("searching");
	const [tableData, setTableData] = useState<TData[]>([]);
	const [fetchedTableData, setFetchedTableData] = useState<TData[]>([]); // For search operations
	const [pageCount, setPageCount] = useState(1);

    const returnedObject = fetchHook({
        limit: 6,
        orderBy: "createdAt:desc",
        page: newData ? 1 : (Number(page) || 1),
        ...options,
        ...generateFilter(searchParams, "filter"),
    });
    
    const { data, isLoading, refetch } = returnedObject as ReturnedObject;

	useEffect(() => {
		if (!isLoading && data?.data) {
			setTableData(data.data);
			setFetchedTableData(data.data);
            setPageCount(data.pagination?.totalPages || 1);
		}
		// eslint-disable-next-line
	}, [isLoading]);
    
	useEffect(() => {
        if (searching) {
            setPageCount(1);
            return
        }
        
        refetch().then((response) => {
            if (response) {
                setTableData(response.data?.data || []);
                setFetchedTableData(response.data?.data || []);
                setPageCount(response.data?.pagination?.totalPages || 1);
            }
        });

        if (newData) {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.delete("newData");
            setSearchParams(currentParams);
        }
        if (reloadData) {
            const currentParams = new URLSearchParams(window.location.search);
            currentParams.delete("reloadData");
            setSearchParams(currentParams);
        }
	}, [searchParams, ...(reloadDeps || [])])

    return {
        tableAPIRef,
        tableData,
        fetchedTableData,
        isLoading,
        pageCount,
        searching,
        setTableData,
        setFetchedTableData
    }
}

/**
 * Custom hook for managing table data with state variables and pagination
 * 
 * This hook handles table data management including loading, filtering, pagination and search functionality.
 * It provides a unified interface for data operations and state management of table components.
 * 
 * @template TData - The type of data being managed in the table
 * 
 * @param {Object} params - The parameters object
 * @param {Function} params.fetchHook - The hook function used to fetch data
 * @param {Object} [params.options] - Additional options to pass to the fetch hook
 * @param {Array} [params.reloadDeps] - Dependencies array that triggers data reload when changed
 * 
 * @returns {Object} An object containing:
 * @returns {GridApiRef} tableAPIRef - Reference to the table API
 * @returns {TData[]} defaultTableData - The original unfiltered table data
 * @returns {TData[]} tableData - The current filtered/processed table data
 * @returns {boolean} isLoading - Loading state indicator
 * @returns {number} page - Current page number
 * @returns {number} pageCount - Total number of pages
 * @returns {Object} tableFilter - Current filter settings
 * @returns {boolean} searching - Search state indicator
 * @returns {Function} setTableData - Function to update table data
 * @returns {Function} setTableFilter - Function to update filter settings
 * @returns {Function} setSearching - Function to update search state
 * @returns {Function} addNewData - Function to trigger new data addition
 * @returns {Function} reloadData - Function to trigger data reload
 * @returns {Function} handlePageChange - Function to handle page changes
 */
export function useManageTableWithStateVariables<TData>({ fetchHook, options, reloadDeps }: ManageTable) {
	const tableAPIRef = useGridApiRef();
	const [defaultTableData, setDefaultTableData] = useState<TData[]>([]);
	const [tableData, setTableData] = useState<TData[]>([]);
	const [tableFilter, setTableFilter] = useState<any>({});
	const [page, setPage] = useState(1);
	const [pageCount, setPageCount] = useState(1);
	const [newData, setNewData] = useState("");
	const [reloadData, setReloadData] = useState("");
	const [searching, setSearching] = useState(false);

    const returnedObject = fetchHook({
        limit: 6,
        orderBy: "createdAt:desc",
        page: newData ? 1 : page,
        ...options,
        ...tableFilter,
    });
    
    const { data, isLoading, refetch } = returnedObject as ReturnedObject;

	useEffect(() => {
		if (!isLoading && data?.data) {
			setTableData(data.data);
			setDefaultTableData(data.data);
            setPageCount(data.pagination?.totalPages || 1);
		}
	}, [isLoading]);
    
	useEffect(() => {
        if (searching) {
            setPageCount(1);
            setTableFilter({})
            return
        }
		newData && setPage(1);
		newData && setTableFilter({});

        refetch().then((response) => {
            if (response) {
                setTableData(response.data?.data || []);
                setDefaultTableData(response.data?.data || []);
                setPageCount(response.data?.pagination?.totalPages || 1);
            }

			newData && setNewData("");
			reloadData && setReloadData("");
        });
	}, [page, newData, reloadData, tableFilter, searching, ...(reloadDeps || [])])

    return {
        tableAPIRef,
        defaultTableData,
        tableData,
        isLoading,
        page,
        pageCount,
        tableFilter,
        searching,
        setTableData,
        setTableFilter,
        setSearching,
        addNewData: () => setNewData("true"),
        reloadData: () => setReloadData("true"),
        handlePageChange: (_, value: number) => setPage(value),
    }
}

/**
 * A custom hook for managing table data with URL search parameters.
 * Provides utilities for handling data operations in table views through URL parameters.
 * 
 * @returns An object containing functions to manage table data:
 *  - `addNewTableData`: Function to update search parameters for new data entry
 *  - `reloadTableData`: Function to trigger table data reload through URL parameters
 * 
 * @example
 * ```typescript
 * const { addNewTableData, reloadTableData } = useManageTableWithSearchParamsData();
 * 
 * // Add new data and reset to page 1
 * addNewTableData({ sortBy: 'name' });
 * 
 * // Reload table data with new parameters
 * reloadTableData({ filter: 'active' });
 * ```
 */
export function useManageTableWithSearchParamsData() {
	const [searchParams, setSearchParams] = useSearchParams();
    const view = searchParams.get("view");

    /**
     * Updates the search parameters to indicate a new data entry and resets the page to 1.
     * This function is commonly used when adding new data to a table view.
     * 
     * @param params - Optional record of key-value pairs to be included in the search parameters
     * @example
     * // Basic usage
     * addNewTableData();
     * 
     * // With additional parameters
     * addNewTableData({ sortBy: 'name', filter: 'active' });
     */
    const addNewTableData = (params?: Record<string, string>) => {
        if (view) {
            setSearchParams({ 
                view, 
                page: "1",
                newData: "true",
                ...(params || {})
            });
        } else {
            setSearchParams({ 
                page: "1", 
                newData: "true",
                ...(params || {})
            });
        }
    };

    /**
     * Reloads the table data by updating URL search parameters
     * @param params - Optional record of key-value pairs to add to URL search params
     * @description This function updates the URL search parameters by setting 'reloadData' to true 
     * and adding any additional parameters provided. It triggers a table reload through URL parameter changes.
     */
    const reloadTableData = (params?: Record<string, string>) => {
        const currentParams = new URLSearchParams(window.location.search);
        currentParams.set("reloadData", "true");
        if (params) {
            for (const [key, value] of Object.entries(params)) {
                currentParams.set(key, value);
            }
        }
        setSearchParams(currentParams);
    };

    return { addNewTableData, reloadTableData }
}

/**
 * Generates a filter object from URL search parameters, excluding specified keys.
 * If a cancel parameter is provided and not present in the search params, returns an empty object.
 * 
 * @param searchParams - The URLSearchParams object containing the query parameters
 * @param cancel - Optional parameter to check if filtering should be cancelled
 * @returns An object containing the filtered key-value pairs from the search parameters
 * 
 * @example
 * const params = new URLSearchParams('name=john&age=25');
 * const filter = generateFilter(params);
 * // Returns: { name: 'john', age: '25' }
 */
export function generateFilter(searchParams: URLSearchParams, cancel?: string) {
    if (cancel && !searchParams.get(cancel)) return {};
    
    let filter: any = {};

    searchParams.forEach((value, key) => {
        if (!excludedKeys.has(key)) {
            filter[key] = value;
        }
    });

    return filter;
}

const excludedKeys = new Set([
	"view", "filter", "page", "reloadData",
    "searching", "newData", "taxRule", "clearSearch"
]);